import React, { ReactElement, useContext, useEffect, useState } from 'react';
import { Select as AntSelect } from 'antd';
import styled from 'styled-components';
import { colorsPalette } from '../../styles/colors-palette';
import { Skeleton } from './Skeleton';
import { ValidationContext } from './Validation';
import UiKit from '../index';

type SelectValueType = string | number | null;
type SelectSizeType = 'large' | 'middle' | 'small';
type FilterOption<ValueType extends SelectValueType> = {
  label: string;
  value: ValueType;
  options?: Omit<FilterOption<ValueType>, 'options'>[];
};
type SelectOption<ValueType extends SelectValueType> = { label: string; value: ValueType };
type SelectGroup<ValueType extends SelectValueType> = {
  label: string;
  children: SelectOption<ValueType>[];
};

enum TypeMultiSelect {
  GROUP,
  SINGLE,
}

interface MultiSelectProps<ValueType extends SelectValueType> {
  selectedValues: ValueType[] | ['all'];
  excludedValues: ValueType[];
  showSearch?: boolean;
  onChange: (selectedValue: ValueType[] | ['all'], excludedValues: ValueType[]) => void;
  onSearch?: (values: string) => void;
  optionFilterProp?: string;
  options: SelectOption<ValueType>[] | SelectGroup<ValueType>[];
  disabled?: boolean;
  filter?: (input: string, option: SelectOption<ValueType> | undefined) => boolean;
  maxTagCount?: number | 'responsive';
  dropdownRender?: (node: React.ReactElement) => React.ReactElement;
  className?: string;
  placeholder?: string;
  size?: SelectSizeType;
  onPageScrolled?: () => void;
  notFoundContent?: React.ReactNode;
  showSelectAll?: boolean;
  selectAllText?: string;
  selectAllCount?: number;
  defaultValue?: ValueType[];
  loading?: boolean;
  searchValue?: string;
}

const Option = AntSelect.Option;
const OptionGroup = AntSelect.OptGroup;

// TODO add "all" to separate type
function NewMultiSelect<ValueType extends SelectValueType>({
  selectedValues = [],
  excludedValues = [],
  showSearch,
  onChange,
  onSearch,
  onPageScrolled,
  options,
  disabled,
  filter,
  maxTagCount,
  dropdownRender,
  className,
  size,
  placeholder,
  optionFilterProp = 'label',
  notFoundContent,
  showSelectAll,
  selectAllText,
  selectAllCount,
  defaultValue,
  loading,
  searchValue,
}: MultiSelectProps<ValueType>): ReactElement {
  const [typeMultiSelect, setTypeMultiSelect] = useState<TypeMultiSelect>();
  const [selectedOptions, setSelectedOptions] = useState<ValueType[]>([]);
  const [excludedOptions, setExcludedOptions] = useState<ValueType[]>([]);
  const [searchValueCurrent, setSearchValueCurrent] = useState<string>(searchValue || '');
  const [isFilterSelectedAll, setIsFilterSelectedAll] = useState<boolean>(false);
  const [isAllSelected, setIsAllSelected] = useState<boolean>(false);
  const { validateStatus } = useContext(ValidationContext);

  const filteredOptionsValue: ValueType[] = [];

  useEffect(() => {
    filterSelected(selectedValues, excludedValues);
  }, [selectedValues, excludedValues]);

  useEffect(() => {
    if (getChildren().length) {
      setTypeMultiSelect(TypeMultiSelect.GROUP);
    } else {
      setTypeMultiSelect(TypeMultiSelect.SINGLE);
    }

    filterSelected(selectedValues, excludedValues);
  }, [options.length]);

  useEffect(() => {
    if (!isFilterSelectedAll && selectedValues.length === filteredOptionsValue.length) {
      setIsFilterSelectedAll(true);
    }
    if (isFilterSelectedAll && selectedValues.length !== filteredOptionsValue.length) {
      setIsFilterSelectedAll(false);
    }
  }, [filteredOptionsValue]);

  const antOnFilterSelectClick = (event: React.UIEvent<HTMLDivElement, UIEvent>) => {
    event.stopPropagation();
    setIsFilterSelectedAll(!isFilterSelectedAll);
    return onChange && onChange(!isFilterSelectedAll ? filteredOptionsValue : [], []);
  };

  const antOnSelect = (value: ValueType) => {
    if (isFilterSelectedAll) {
      setIsFilterSelectedAll(false);
    }

    if (value === 'all') {
      setIsAllSelected(true);

      return onChange && onChange(['all' as ValueType], []);
    }

    if (isAllSelected) {
      onChange &&
        onChange(
          ['all' as ValueType],
          excludedOptions.filter(option => option !== value),
        );
      return;
    }

    onChange &&
      onChange(
        [...selectedOptions, value],
        isAllSelected ? excludedOptions.filter(option => option !== value) : [],
      );
  };

  const antOnDeselect = (value: ValueType) => {
    if (value === 'all') {
      setIsAllSelected(false);

      return onChange && onChange([], []);
    }

    onChange &&
      onChange(
        isAllSelected ? ['all' as ValueType] : selectedOptions.filter(option => option !== value),
        isAllSelected ? [...excludedOptions, value] : [],
      );
  };

  const getChildren = () => {
    const children: SelectOption<ValueType>[] = [];
    options.forEach(option => {
      if (option.children?.length) {
        children.push(...option.children);
      }
    });

    return children;
  };

  const filterSelected = (selected: ValueType[] | ['all'], excluded: ValueType[]) => {
    let filterSelected: ValueType[] = selected as ValueType[];
    let isAllSelected = false;
    if (showSelectAll) {
      if (selected[0] === 'all') {
        filterSelected = (getChildren().length ? getChildren() : options).map(
          option => option.value,
        );
        isAllSelected = true;
      } else {
        isAllSelected = options.length ? filterSelected.length === options.length : false;
      }
      setIsAllSelected(isAllSelected);
    }

    filterSelected = filterSelected.filter(item => !excluded.includes(item));
    setSelectedOptions(filterSelected);
    setExcludedOptions(isAllSelected ? excluded : []);
  };

  // TODO maybe need to inc size(32) every scroll turn
  const antPopupScroll = (event: React.UIEvent<HTMLDivElement, UIEvent>) => {
    const target: HTMLDivElement = event.target as HTMLDivElement;
    if (target.scrollTop + target.offsetHeight >= target.scrollHeight - 32) {
      onPageScrolled && onPageScrolled();
    }
  };

  const antOnSearch = (values: string): void => {
    setSearchValueCurrent(values);
    onSearch && onSearch(values);
  };

  const antFilterOptions = (
    input: string,
    option: FilterOption<ValueType> | undefined,
  ): boolean => {
    if (option && option.value === 'allFiltered') {
      return true;
    }

    if (filter) {
      const filtered = filter(input, option);
      if (filtered) {
        filteredOptionsValue.push(option?.value as ValueType);
      }
      return filtered;
    }

    if (option) {
      const isGroup = Array.isArray(option.options);
      if (isGroup) {
        return false;
      }
    }

    if (option) {
      const filtered = option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0;

      if (filtered) {
        filteredOptionsValue.push(option?.value as ValueType);
      }

      return filtered;
    }
    return false;
  };

  if (loading) {
    return <StyledSkeleton className={className} />;
  }

  return (
    <StyledSelect
      mode={'multiple'}
      value={isAllSelected ? ['all' as ValueType, ...selectedOptions] : selectedOptions}
      onSelect={antOnSelect}
      onDeselect={antOnDeselect}
      onSearch={antOnSearch}
      status={validateStatus}
      autoClearSearchValue={false}
      optionFilterProp={optionFilterProp}
      onPopupScroll={antPopupScroll}
      placeholder={placeholder}
      filterOption={antFilterOptions}
      showSearch={showSearch}
      defaultValue={defaultValue}
      disabled={disabled}
      className={className}
      searchValue={searchValueCurrent}
      size={size ? size : 'large'}
      maxTagCount={isAllSelected ? 1 : maxTagCount}
      dropdownRender={dropdownRender}
      notFoundContent={notFoundContent}
    >
      {searchValueCurrent && filteredOptionsValue && (
        <Option
          value={'allFiltered'}
          label={selectAllText ? selectAllText : 'Все результаты'}
          className={isFilterSelectedAll ? 'ant-select-item-option-selected' : ''}
        >
          <SelectAllOption
            onClick={event => {
              antOnFilterSelectClick(event);
            }}
          >
            <OptionLabel>
              {selectAllText ? selectAllText : 'Все результаты'}{' '}
              {selectAllCount ? `(${selectAllCount})` : ''}
            </OptionLabel>

            {isFilterSelectedAll && (
              <SelectArrow className={'ant-select-item-option-state'}>
                <UiKit.icons.Check />
              </SelectArrow>
            )}
          </SelectAllOption>
        </Option>
      )}
      {showSelectAll && !!options.length && !searchValueCurrent && (
        <Option
          className={'test'}
          key={'all'}
          label={selectAllText || 'Все результаты'}
          value={'all'}
        >
          {selectAllText || 'Все результаты'} {selectAllCount ? `(${selectAllCount})` : ''}
        </Option>
      )}
      {options.map((option, index) => {
        if (!option.children?.length) {
          return (
            <Option key={option.value} value={option.value} label={option.label}>
              <OptionContentWrapper>
                <OptionLabel>{option.label}</OptionLabel>
              </OptionContentWrapper>
            </Option>
          );
        }
        return (
          <OptionGroup key={option.value} label={option.label} value={option.value}>
            {option.children.map(child => (
              <Option key={child.value} value={child.value} label={child.label}>
                <OptionContentWrapper>
                  <OptionLabel>{child.label}</OptionLabel>
                </OptionContentWrapper>
              </Option>
            ))}
          </OptionGroup>
        );
      })}
    </StyledSelect>
  );
}

const StyledSelect = styled(AntSelect)`
  &.ant-select-multiple.ant-select-lg
    .ant-select-selection-overflow-item.ant-select-selection-overflow-item-rest {
    display: ${props => (props.maxTagCount === 1 ? 'none' : 'inline-flex')};
  }
  &.ant-select {
    width: 100%;

    &:not(.ant-select-disabled) {
      &:not(.ant-select-customize-input) {
        &:not(.ant-pagination-size-changer) {
          &:hover {
            .ant-select-selector {
              border-color: ${colorsPalette.dustyBlue500};
            }
          }
        }
      }
    }
  }

  &.ant-select-focused {
    &:not(.ant-select-disabled) {
      &:not(.ant-select-customize-input) {
        &:not(.ant-pagination-size-changer) {
          .ant-select-selector {
            border-color: ${colorsPalette.dustyBlue500};
          }
        }
      }
    }
  }
` as typeof AntSelect;

const OptionContentWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
const OptionLabel = styled.div``;

const StyledSkeleton = styled(Skeleton)`
  &.ant-skeleton.ant-skeleton-element .ant-skeleton-input {
    height: 40px;
    vertical-align: middle;
    border-radius: 8px;
  }
`;

const SelectAllOption = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SelectArrow = styled.span``;

export default NewMultiSelect;
